/**
 * Das Gesetzbuch — die Paragraphen als Gesetzestext, mit den geltenden
 * Parametern aus der config eingeblendet. Die Zahlen kommen vom Server:
 * beschließt die Gruppe eine Novelle, stimmt der Text von selbst wieder.
 */
import type { ReactNode } from 'react'
import { api } from '../api/client.js'
import { useLaden } from '../api/laden.js'
import type { Gesetzbuch as GesetzbuchDaten } from '../api/typen.js'
import { Fehlermeldung, Laufvermerk } from '../bausteine/Bausteine.js'

function Wert({ children }: { children: ReactNode }) {
  return <strong className="font-akte font-bold text-messing">{children}</strong>
}

function Paragraph({
  nummer,
  titel,
  children,
}: {
  nummer: string
  titel: string
  children: ReactNode
}) {
  return (
    <article className="border-l-2 border-messing/30 pl-4">
      <h2 className="font-display text-lg tracking-wide text-lampe">
        {nummer} {titel}
      </h2>
      <div className="mt-2 flex flex-col gap-2 text-sm leading-relaxed text-akte/90">{children}</div>
    </article>
  )
}

export function Gesetzbuch() {
  const gesetz = useLaden(() => api.get<GesetzbuchDaten>('/api/gesetzbuch'))

  if (gesetz.laedt) return <Laufvermerk />
  if (gesetz.fehler) return <Fehlermeldung text={gesetz.fehler} />
  const g = gesetz.daten
  if (!g) return null

  const p1 = g.paragraph1
  const p3 = g.paragraph3
  const p4 = g.paragraph4
  const p5 = g.paragraph5
  const p6 = g.paragraph6
  const p9 = g.paragraph9
  const uhr = g.schnapsuhr

  return (
    <>
      <section className="pt-8 text-center">
        <p className="protokoll">In der Fassung der letzten Novelle</p>
        <h1 className="urteil mt-4">
          Strafschnaps-
          <br />
          gesetzbuch
        </h1>
        <p className="mt-4 text-sm text-akte/75">
          Eine Einheit beträgt <Wert>{g.unitCl} cl</Wert>. Das Tageslimit liegt bei{' '}
          <Wert>{g.dailyLimitUnits} Einheiten</Wert> pro Person; darüber hinaus wird gestundet
          statt getrunken.
        </p>
      </section>

      <div className="mt-10 flex flex-col gap-8 pb-4">
        <Paragraph nummer="§ 1" titel="Strafschnaps">
          <p>
            Wer zu einem regulären Event unangekündigt zu spät erscheint, wird bestraft: unter{' '}
            <Wert>{p1.ersteEinheitAbMin} Minuten</Wert> sieht die Kammer ab; ab{' '}
            <Wert>{p1.ersteEinheitAbMin} Minuten</Wert> erkennt sie auf{' '}
            <Wert>{p1.grundeinheiten} Einheit</Wert>; jede weitere angefangene Stunde ab{' '}
            <Wert>{p1.steigerungAbMin} Minuten</Wert> erhöht um{' '}
            <Wert>{p1.steigerungEinheiten} Einheit</Wert>.
          </p>
        </Paragraph>

        <Paragraph nummer="§ 2" titel="Auswahl des Schnapses">
          <p>
            Die Sorte wählen die anderen Mitglieder — niemals der Bestrafte selbst. Zur Wahl stehen
            der Zufallsrichter, den die App aus den Anwesenden lost, und das Schnapsroulette über
            den Katalog.
          </p>
        </Paragraph>

        <Paragraph nummer="§ 3" titel="Verzehrzeit und Schnapszins">
          <p>
            Wird eine Strafe nicht am selben Kalendertag getilgt, wächst sie je angefangener Woche
            um <Wert>{p3.einheitenProIntervall} Einheit</Wert>. Die Kammer bewertet offene Posten
            täglich um drei Uhr nachts neu{p3.aktiv ? '' : ' (derzeit ausgesetzt)'}.
          </p>
        </Paragraph>

        <Paragraph nummer="§ 4" titel="Sonderschnapsevent">
          <p>
            Ein Sonderschnapsevent ist mindestens <Wert>{p4.ankuendigungsfristStunden} Stunden</Wert>{' '}
            vorher anzukündigen; andernfalls ist der Termin als solches unzulässig. Die erste
            Einheit fällt bereits ab <Wert>{p4.ersteEinheitAbMin} Minuten</Wert> an, und die gesamte
            errechnete Strafe wird <Wert>×{p4.faktor}</Wert> genommen.
          </p>
        </Paragraph>

        <Paragraph nummer="§ 5" titel="Nichtteilnahme">
          <p>
            Eine Absage hat spätestens <Wert>{p5.absagefristStunden} Stunden</Wert> vor Beginn in
            der App zu erfolgen. Wer ohne fristgerechte Absage fehlt, wird beim Schluss der Sitzung
            mit <Wert>{p5.einheitenBeiUnentschuldigtemFehlen} Einheiten</Wert> belegt.
          </p>
        </Paragraph>

        <Paragraph nummer="§ 6" titel="Angleichsschnaps">
          <p>
            Wer seine Verspätung fristgerecht ankündigt, schuldet pauschal{' '}
            <Wert>{p6.angleichsschnapsEinheiten} Einheit</Wert> zuzüglich{' '}
            <Wert>{p6.anteilRegulaererStrafe * 100} %</Wert> der regulären Strafrate, gerundet auf
            halbe Einheiten.
          </p>
        </Paragraph>

        <Paragraph nummer="§ 7" titel="Rückwirkende Berechnung">
          <p>
            Wer später erscheint als angesagt, wird rückwirkend nach der Logik des § 4 berechnet —
            strengere Schwelle und Verdopplung. § 6 findet dann keine Anwendung.
          </p>
        </Paragraph>

        <Paragraph nummer="§ 8" titel="Schnapszahläquivalent">
          <p>
            Die angesagte Verspätung ist bindend. Wer früher erscheint als angekündigt, schuldet
            gleichwohl die Menge, die der Ansage entspricht.
          </p>
        </Paragraph>

        <Paragraph nummer="§ 9" titel="Zufallsklausel">
          <p>
            Bei jeder Buchung zieht die Kammer mit einer Wahrscheinlichkeit von{' '}
            <Wert>{Math.round(p9.wahrscheinlichkeit * 100)} %</Wert> einen Modifier. Der
            Schnapszahl-Bonus greift bei einem Kontostand von{' '}
            <Wert>{p9.schnapszahlen.join(', ')}</Wert>.
          </p>
          <ul className="flex flex-col gap-1 pl-4">
            {p9.modifiers.map((m) => (
              <li key={m.key} className="list-disc marker:text-messing">
                <span className="text-lampe">{m.label}</span> — {m.beschreibung}
                {m.gewicht !== 1 && <span className="text-akte/60"> (Gewicht {String(m.gewicht).replace('.', ',')})</span>}
              </li>
            ))}
          </ul>
          <p>
            Alle Ziehungen erfolgen serverseitig und werden protokolliert. Ein Neuwürfeln durch
            Neuladen ist ausgeschlossen.
          </p>
        </Paragraph>

        <Paragraph nummer="§ 10" titel="Schnapsuhr">
          <p>
            Während einer Sitzung läuft eine Zufallsuhr, die in unregelmäßigen Abständen zum
            Ausschank ruft. Der Zeitpunkt wird aus dem Fenster von <Wert>{uhr.tMinMin}</Wert> bis{' '}
            <Wert>{uhr.tMaxMin} Minuten</Wert> gezogen, mit ansteigender Dichte (Steilheit{' '}
            <Wert>{uhr.k}</Wert>); frühe Schläge sind unwahrscheinlich, späte wahrscheinlich. Je
            Sitzung schlägt die Uhr höchstens <Wert>{uhr.maxRounds} Mal</Wert>, je Schlag{' '}
            <Wert>{uhr.einheitenProRunde} Einheit</Wert>{' '}
            {uhr.mode === 'runde'
              ? 'für alle Anwesenden'
              : `für ${uhr.zufallsopferAnzahl} Ausgeloste — allein trinken macht keinen Spaß`}
            . Die erste Ziehung erfolgt mit dem ersten Check-in; die Länge jeder Runde wird
            zufällig gezogen, der Countdown zur Fälligkeit ist sichtbar. Wer die Sitzung
            verlassen hat, wird nicht mehr getroffen; die Uhr lässt sich je Sitzung abschalten.
          </p>
        </Paragraph>

        {g.paragraph11.aktiv && (
          <Paragraph nummer="§ 11" titel="Schnapsbattle (Novelle)">
            <p>
              Zwei oder mehr Anwesende dürfen sich im Würfelspiel duellieren: je Runde wirft jeder{' '}
              <Wert>{g.paragraph11.wuerfelProSpieler} Würfel</Wert>; wer allein die niedrigste
              Summe hat, verliert. Teilen sich mehrere die niedrigste Summe, stechen nur diese
              weiter. Der Verlierer schuldet{' '}
              <Wert>
                {g.paragraph11.einheitenFuerVerlierer}{' '}
                {g.paragraph11.einheitenFuerVerlierer === 1 ? 'Einheit' : 'Einheiten'}
              </Wert>{' '}
              und dreht am Schnapsrad. Gefordert wird persönlich, angenommen ebenso — und
              gewürfelt wird vom eigenen Gerät, die Augen fallen auf dem Server. Das Protokoll ist
              vollständig.
            </p>
          </Paragraph>
        )}
      </div>
    </>
  )
}
